import _ from 'lodash';
import React, { Component } from 'react';
import objectHash from 'object-hash';
import Chart from './Chart';
import { moneyFormatFromCents } from '../../utilities/moneyFormat';
import MoneyComponent from '../elements/MoneyComponent';
import CurrencyInput from '../elements/CurrencyInput';
import LoanGraphData from '../../models/LoanGraphData';
import LadderLocalStorage from '../../utilities/LadderLocalStorage';
import CircleLoader from './CircleLoader';
import TextInput from "../elements/TextInput.jsx";
import AdHocLoan from "../../models/AdHocLoan.js";
import InputSwitch from "./InputSwitch.jsx";
import MaterialIcon from "../elements/MaterialIcon.jsx";
import NoResults from "../elements/NoResults.jsx";
import projectPayoff, { strategies, types } from "../../utilities/loanPayoffProjection.js";
import { DownPaymentSummary, downPaymentParts, LoanMonthEditor, LoanTermsDetails } from "./LoanTerms.jsx";
import InfoTip from "../elements/InfoTip.jsx";

const strategyDetails = {
	[strategies.MINIMUMS]: { name: 'As is', blurb: 'Each loan keeps its own payment' },
	[strategies.AVALANCHE]: { name: 'Avalanche', blurb: 'Extra goes to the highest rate first' },
	[strategies.SNOWBALL]: { name: 'Snowball', blurb: 'Extra goes to the smallest balance first' },
};

/** What each figure on the Debt and Savings tabs means, for the help icon beside its label. */
export const metricTips = {
	debtFree: 'The month the last open loan is paid off, if payments carry on as projected.',
	payingAMonth: 'What the projection pays toward principal and interest each month, or a strategy\'s monthly budget when that is higher. Minimum is the loans\' minimums added up. Last paid is what they were last actually paid, escrow and fees included.',
	leftToPay: 'The principal and interest the projection pays from the start of this month until every loan is paid off. Escrow and fees are not counted.',
	interestToCome: 'The interest the projection charges from the start of this month until every loan is paid off.',
	savedInvested: 'Every account\'s balance as last recorded in the budget, added up.',
	interestReceived: 'Interest at each account\'s rate, from its first recorded balance through the years looked at.',
	loanBalance: 'What is owed at the end of this month: the balance recorded in the budget, or the projected one when none was recorded.',
	savingsBalance: 'What the account holds at the end of this month: the balance recorded in the budget, or the projected one when none was recorded.',
	paid: 'What was paid toward this loan, from its category\'s transactions, escrow and fees included. When this month has none yet, it is the latest of the two months before.',
	principalAndInterest: 'The part of a payment that pays down the balance and its interest, and what the projection pays from here: the minimum, or what the balances show it has been paid lately when that is more and "Assume recent payments continue" is on.',
	interestCost: 'Interest under the current terms, from when they began until the loan is paid off: worked out from the balances for the months behind, projected for the months ahead.',
	interestEarned: 'Interest at the account\'s rate, from its first recorded balance through the years looked at.',
	loanPrincipal: 'The balance the current terms began at: the first balance recorded, or the balance when the rate last changed, as in a refinance.',
	savingsPrincipal: 'The first balance recorded for the account.',
	downPayment: 'Paid up front when the loan opened, cash and credits, as recorded on its terms.',
	schedulePaid: 'The transactions in this category that month, escrow and fees included.',
	schedulePrincipalAndInterest: 'What the month\'s payment did to the balance and its interest. A month with a recorded balance is worked out from it; any other month is the projected payment, so the month after a gap can show a catch-up.',
	scheduleInterest: 'A month\'s interest on the balance, at the loan\'s rate.',
	scheduleBalance: 'At the end of the month. A * marks a balance recorded in the budget; the rest are projected.',
};

export default class LoanPayoff extends Component {
	static strategies = strategies;

	static types = types;

	constructor(props) {
		super(props);
		const { type } = this.props;

		this.keyForAmountToAddPerMonth = type === LoanPayoff.types.SAVINGS ? 'savingsMonthlyAdd' : 'loanPayoffMonthlyPayment';

		this.onLegendClick = this.onLegendClick.bind(this);
		this.estimatedPaymentChange = this.estimatedPaymentChange.bind(this);
		this.strategyChange = this.strategyChange.bind(this);
		this.assumeRecentPaymentsChange = this.assumeRecentPaymentsChange.bind(this);

		this.state = {
			monthlyPayment: LadderLocalStorage.getItem(this.keyForAmountToAddPerMonth, 0),
			strategy: type === LoanPayoff.types.SAVINGS ? LoanPayoff.strategies.MINIMUMS : LadderLocalStorage.getItem('loanPayoffStrategy', LoanPayoff.strategies.MINIMUMS),
			assumeRecentPayments: LadderLocalStorage.getItem('loanPayoffAssumeRecentPayments', true) !== false,
			projection: null,
			comparison: {},
			dataHash: null,
			graphOptions: null,
			disabledGraphs: {},
			yearsToExplore: LadderLocalStorage.getItem('savingsYearsToExplore', 5),
			firstLoad: true,
			adHocLoans: LadderLocalStorage.getItem('adHocLoans', []),
		};
		this.state.adHocLoans.push({});

		this.state.adHocLoans.forEach((adHocLoan, index) => {
			this.state.adHocLoans[index] = AdHocLoan.unserialize(adHocLoan);
		});

		this.updateAdHocLoan = this.updateAdHocLoan.bind(this);
		this.recalculateGraphData = _.debounce(this.recalculateGraphData, 100, {});

		this.yearsToExploreChange = this.yearsToExploreChange.bind(this);
	}

	yearsToExploreChange(event) {
		const newValue = event.target.value;
		LadderLocalStorage.setItem('savingsYearsToExplore', newValue);
		this.setState({
			yearsToExplore: newValue
		}, this.recalculateGraphData);
	}

	onLegendClick(event, legendTarget) {
		const index = legendTarget.datasetIndex;
		const disabledGraphs = { ...this.state.disabledGraphs };
		if(!legendTarget.hidden) {
			disabledGraphs[index] = true;
		} else {
			delete disabledGraphs[index];
		}
		this.setState({ disabledGraphs }, this.recalculateGraphData);
	}

	componentDidMount() {
		this.recalculateGraphData();
	}

	componentDidUpdate(prevProps, prevState) {
		if(prevProps.selectedDate && this.props.selectedDate && !prevProps.selectedDate.isSame(this.props.selectedDate, 'month')) {
			this.recalculateGraphData();
			return;
		}
		if(prevState.monthlyPayment !== this.state.monthlyPayment) {
			this.recalculateGraphData();
			return;
		}
		// A reload builds new groups, with the categories and budget rows in them. That is how a
		// loan's terms edited on its card come back to be drawn.
		const before = prevProps.debtCategoryGroups || [];
		const after = this.props.debtCategoryGroups || [];
		if(before.length !== after.length || after.some((group, index) => group !== before[index])) {
			this.recalculateGraphData();
		}
	}

	updateAdHocLoan(loan, update) {
		_.each(update, (value, key) => {
			loan[key] = value;
		});
		const serializedLoans = [];
		let lastIsPopulated = false;
		this.state.adHocLoans.forEach((adHocLoan, index) => {
			const isLast = index === this.state.adHocLoans.length - 1;
			if(!adHocLoan.isEmpty()) {
				serializedLoans.push(adHocLoan.serialize());
			}
			if(isLast && !adHocLoan.isEmpty()) {
				lastIsPopulated = true;
			}
		});
		LadderLocalStorage.setItem('adHocLoans', serializedLoans);
		this.recalculateGraphData();
		if(lastIsPopulated) {
			this.setState({
				adHocLoans: [...this.state.adHocLoans, new AdHocLoan()]
			});
		}

		this.forceUpdate();
	}

	strategyChange(newValue) {
		LadderLocalStorage.setItem('loanPayoffStrategy', newValue);
		this.setState({
			strategy: newValue
		}, this.recalculateGraphData);
	}

	assumeRecentPaymentsChange(event) {
		const assumeRecentPayments = event.target.checked;
		LadderLocalStorage.setItem('loanPayoffAssumeRecentPayments', assumeRecentPayments);
		this.setState({ assumeRecentPayments }, this.recalculateGraphData);
	}

	estimatedPaymentChange(amount) {
		LadderLocalStorage.setItem(this.keyForAmountToAddPerMonth, amount);
		this.setState({
			monthlyPayment: amount
		});
	}

	/** The categories to graph: the loans in the groups handed in, and any ad hoc loans. */
	loansToProject() {
		const { debtCategoryGroups, type } = this.props;
		const categories = [];
		for(let categoryGroup of debtCategoryGroups) {
			for(let category of categoryGroup.getCategories()) {
				// The debt tab is handed the expense groups too, for a loan budgeted in one of them.
				// Their other categories are not debts, whatever data they carry.
				if(type === LoanPayoff.types.LOANS && !category.isLoanCategory(categoryGroup)) {
					continue;
				}
				if(category.getBudgetsInOrder().some((budget) => budget.notes === 'null')) {
					continue;
				}
				categories.push(category);
			}
		}
		return [...this.state.adHocLoans, ...categories];
	}

	recalculateGraphData() {
		const { selectedDate, type } = this.props;
		const { strategy, yearsToExplore, assumeRecentPayments, monthlyPayment, disabledGraphs } = this.state;
		this.setState({
			firstLoad: false
		});

		if(!selectedDate) {
			return;
		}
		const loans = this.loansToProject();
		const options = {
			categories: loans,
			type,
			monthlyPayment,
			selectedDate,
			yearsToExplore,
			assumeRecentPayments,
			disabledGraphs,
		};
		const projection = projectPayoff({ ...options, strategy });
		if(!projection) {
			this.setState({ projection: null, comparison: {} });
			return;
		}

		// Every strategy is run, so the page can say what each would do against the others.
		const comparison = {};
		if(type === LoanPayoff.types.LOANS) {
			for(let otherStrategy of Object.values(LoanPayoff.strategies)) {
				comparison[otherStrategy] = otherStrategy === strategy ? projection : projectPayoff({ ...options, strategy: otherStrategy });
			}
		}

		this.setState({
			dataHash: objectHash(projection.data),
			projection,
			comparison,
			graphOptions: LoanPayoff.graphOptions(projection.data, selectedDate),
		});
	}

	static graphOptions(data, selectedDate) {
		const today = selectedDate.format('MMM YYYY');
		const narrow = typeof window !== 'undefined' && window.innerWidth < 600;
		return {
			maintainAspectRatio: false,
			responsive: true,
			legend: {
				position: 'bottom',
				labels: {
					boxWidth: 12,
					usePointStyle: true,
					padding: 16,
				},
			},
			annotation: data.labels.includes(today) ? {
				annotations: [{
					type: 'line',
					mode: 'vertical',
					scaleID: 'x-axis-0',
					value: today,
					borderColor: 'rgba(0, 0, 0, 0.35)',
					borderWidth: 1,
					borderDash: [4, 4],
					label: {
						content: 'Today',
						enabled: true,
						position: 'top',
						backgroundColor: 'rgba(0, 0, 0, 0.6)',
						fontStyle: 'normal',
						fontSize: 11,
					},
				}],
			} : undefined,
			tooltips: {
				intersect: false,
				mode: 'index',
				callbacks: {
					label: LoanPayoff.graphTooltipFormat,
					afterLabel: LoanPayoff.graphTooltipPayment
				}
			},
			elements: {
				line: { borderWidth: 2 },
				point: { radius: 0, hoverRadius: 4, hitRadius: 6 },
			},
			scales: {
				xAxes: [{
					gridLines: { display: false },
					ticks: { autoSkip: true, maxTicksLimit: narrow ? 4 : 10, maxRotation: 0 },
				}],
				yAxes: [{
					gridLines: { color: 'rgba(0, 0, 0, 0.06)', drawBorder: false },
					ticks: {
						beginAtZero: true,
						maxTicksLimit: 6,
						callback: LoanPayoff.axisMoneyFormat,
					}
				}]
			},
			plugins: {
				zoom: {
					pan: {
						enabled: true,
						mode: 'xy',
						speed: 10,
						threshold: 10
					},
					zoom: {
						enabled: true,
						mode: 'xy'
					}
				}
			},
		};
	}

	/** Axis labels in whole dollars, thousands shortened: $120k rather than $120,000.00. */
	static axisMoneyFormat(value) {
		const dollars = value / 100;
		if(Math.abs(dollars) >= 1000) {
			return `$${Math.round(dollars / 1000)}k`;
		}
		return `$${Math.round(dollars)}`;
	}

	static graphTooltipFormat(tooltipData, d) {
		const dataSetName = d.datasets[tooltipData.datasetIndex].label;
		return `${dataSetName} --- ${moneyFormatFromCents(tooltipData.yLabel)}`;
	}

	/**
	 * What was paid that month, from its transactions, with the part the balance accounts for. A month
	 * without any, the future among them, shows the payment the projection made: the loan's payment,
	 * plus any extra the strategy sent there.
	 */
	static graphTooltipPayment(tooltipData, d) {
		const { payments, paid } = d.datasets[tooltipData.datasetIndex];
		const payment = payments ? payments[tooltipData.index] : null;
		const paidThen = paid ? paid[tooltipData.index] : null;
		if(paidThen) {
			if(!payment) {
				return `Paid ${moneyFormatFromCents(paidThen)}`;
			}
			return `Paid ${moneyFormatFromCents(paidThen)}, ${moneyFormatFromCents(payment)} to principal & interest`;
		}
		if(payment === null || payment === undefined) {
			return '';
		}
		return `Payment ${moneyFormatFromCents(payment)}`;
	}

	render() {
		const { type, selectedDate } = this.props;
		const {
			firstLoad, strategy, projection, comparison, graphOptions, monthlyPayment, dataHash, yearsToExplore,
			adHocLoans, assumeRecentPayments,
		} = this.state;
		const isLoans = type === LoanPayoff.types.LOANS;

		if(firstLoad) {
			return (
				<CircleLoader>
					<h4>Loading Graph</h4>
				</CircleLoader>
			);
		}

		return (
			<div className='loan_payoff'>
				<header className='loan_payoff__header'>
					<div>
						<h5 className='loan_payoff__title'>{isLoans ? 'Debt payoff' : 'Savings growth'}</h5>
						{isLoans && !!projection?.debtFreeDate &&
						<div className='loan_payoff__subtitle'>
							{projection.paidOff ? 'Debt free by' : 'Still owing in'}{' '}
							<strong>{projection.debtFreeDate.format('MMMM YYYY')}</strong>
							{' '}{strategy === LoanPayoff.strategies.MINIMUMS ? 'with your payments as they are' : `with the ${strategyDetails[strategy].name.toLowerCase()}`}
						</div>
						}
					</div>
					{isLoans &&
					<label className='loan_payoff__toggle'>
						<input
							type='checkbox'
							className='filled-in'
							onChange={this.assumeRecentPaymentsChange}
							checked={assumeRecentPayments} />
						<span>Assume recent payments continue</span>
					</label>
					}
				</header>

				<section className='loan_payoff__card loan_payoff__chart'>
					{!!projection &&
					<Chart
						key={dataHash}
						onLegendClick={this.onLegendClick}
						type='line'
						data={projection.data}
						options={graphOptions}
					/>
					}
					{!projection &&
					<NoResults>
						No {isLoans ? 'loans' : 'savings'} to graph
					</NoResults>
					}
				</section>

				{isLoans && assumeRecentPayments &&
				<p className='loan_payoff__hint'>
					A loan you have been paying more than its minimum is projected at that amount, worked out
					from its last {LoanGraphData.recentPaymentMonths} months of balances.
				</p>
				}

				{!!projection && projection.warnings.length > 0 &&
				<div className='loan_payoff__warnings'>
					{projection.warnings.map((warning) => (
						<div key={warning}><MaterialIcon>warning</MaterialIcon>{warning}</div>
					))}
				</div>
				}

				{!!projection && isLoans &&
				<React.Fragment>
					<div className='loan_payoff__stats'>
						<Stat label='Debt free' tip={metricTips.debtFree}>
							{projection.debtFreeDate ? projection.debtFreeDate.format('MMM YYYY') : '—'}
						</Stat>
						<Stat label='Paying a month' tip={metricTips.payingAMonth} note={
							<React.Fragment>
								Minimum <MoneyComponent amount={projection.estimatedMinimum} />
								{projection.lastPaid !== null && <React.Fragment> · last paid <MoneyComponent amount={projection.lastPaid} /></React.Fragment>}
							</React.Fragment>
						}>
							<MoneyComponent amount={projection.monthlyBudget ?? projection.currentPayments} />
						</Stat>
						<Stat label={`Left to pay from ${selectedDate.format('MMM')}`} tip={metricTips.leftToPay}>
							<MoneyComponent amount={projection.paidFromSelected} />
						</Stat>
						<Stat label='Interest still to come' tip={metricTips.interestToCome}>
							<MoneyComponent amount={projection.interestFromSelected} showNegative={true} />
						</Stat>
					</div>

					<h6 className='loan_payoff__section'>Strategy</h6>
					<div className='loan_payoff__strategies'>
						{Object.values(LoanPayoff.strategies).map((option) => (
							<StrategyCard
								key={option}
								strategy={option}
								projection={comparison[option]}
								baseline={comparison[LoanPayoff.strategies.MINIMUMS]}
								selected={option === strategy}
								onSelect={this.strategyChange}
							/>
						))}
					</div>

					{strategy !== LoanPayoff.strategies.MINIMUMS &&
					<div className='loan_payoff__budget'>
						<div className='loan_payoff__budget-input'>
							<CurrencyInput
								onChange={this.estimatedPaymentChange}
								type='text'
								name='amountEdit'
								value={monthlyPayment}
								label='Monthly budget for debt'
							/>
						</div>
						<p className='loan_payoff__hint'>
							Anything above what your loans are paying now
							(<MoneyComponent amount={projection.currentPayments} />)
							goes to the {strategy === LoanPayoff.strategies.AVALANCHE ? 'highest rate' : 'smallest balance'}.
							Less than that changes nothing; each paid off loan's payment rolls on to the next.
						</p>
					</div>
					}
				</React.Fragment>
				}

				{!!projection && !isLoans &&
				<React.Fragment>
					<div className='loan_payoff__stats'>
						<Stat label='Saved / invested' tip={metricTips.savedInvested}>
							<MoneyComponent amount={projection.totalInvested} />
						</Stat>
						<Stat label='Interest received' tip={metricTips.interestReceived}>
							<MoneyComponent showNegative={true} amount={projection.totalInterestPaid} />
						</Stat>
					</div>
					<div className='loan_payoff__years'>
						<span>Looking {yearsToExplore} years ahead</span>
						<input type='range'
						       value={yearsToExplore}
						       onChange={this.yearsToExploreChange}
						       min='1' max='50'
						/>
					</div>
				</React.Fragment>
				}

				{!!projection &&
				<React.Fragment>
					<h6 className='loan_payoff__section'>{isLoans ? 'Loans' : 'Accounts'}</h6>
					<div className='loan_payoff__loans'>
						{[...projection.allLoans].sort((a, b) => {
							return a.lastPaymentDate.isAfter(b.lastPaymentDate) ? -1 : 1;
						}).map((loan) => (
							<LoanSummaryChart
								type={type}
								key={loan.category.id}
								loan={loan}
								selectedDate={selectedDate}
							/>
						))}
					</div>
				</React.Fragment>
				}

				{isLoans &&
				<details className='loan_payoff__card loan_payoff__what_if'>
					<summary>What if: add a loan that isn't in the budget</summary>
					<div className='ad_hoc_loans'>
						{adHocLoans.map((loan) => (
							<AdHocLoanComponent
								key={loan.id}
								loan={loan}
								updateLoan={this.updateAdHocLoan}
							/>
						))}
					</div>
				</details>
				}
			</div>
		);
	}
}

function Stat({ label, tip, note, children }) {
	return (
		<div className='loan_payoff__stat'>
			<div className='loan_payoff__stat-label'>{label}{tip && <InfoTip label={`What ${label} means`}>{tip}</InfoTip>}</div>
			<div className='loan_payoff__stat-value'>{children}</div>
			{note && <div className='loan_payoff__stat-note'>{note}</div>}
		</div>
	);
}

/** One strategy, as a card to pick it by: when it clears the debt and what it saves against paying as is. */
export function StrategyCard({ strategy, projection, baseline, selected, onSelect }) {
	const details = strategyDetails[strategy];
	const monthsSooner = projection?.debtFreeDate && baseline?.debtFreeDate ? baseline.debtFreeDate.diff(projection.debtFreeDate, 'months') : 0;
	const interestSaved = projection && baseline ? Math.round(baseline.interestFromSelected - projection.interestFromSelected) : 0;
	return (
		<button
			type='button'
			className={`btn-flat loan_payoff__strategy ${selected ? 'is-selected' : ''}`}
			aria-pressed={selected}
			onClick={() => onSelect(strategy)}
		>
			<span className='loan_payoff__strategy-name'>{details.name}</span>
			<span className='loan_payoff__strategy-blurb'>{details.blurb}</span>
			{!!projection?.debtFreeDate &&
			<span className='loan_payoff__strategy-date'>{projection.debtFreeDate.format('MMM YYYY')}</span>
			}
			{!!projection &&
			<span className='loan_payoff__strategy-detail'>
				<MoneyComponent amount={projection.interestFromSelected} /> interest
			</span>
			}
			{strategy !== strategies.MINIMUMS && (monthsSooner > 0 || interestSaved > 0) &&
			<span className='loan_payoff__strategy-saving'>
				{monthsSooner > 0 && <span>{monthsSooner} mo sooner</span>}
				{monthsSooner > 0 && interestSaved > 0 && ' · '}
				{interestSaved > 0 && <span>saves {moneyFormatFromCents(interestSaved)}</span>}
			</span>
			}
			{strategy !== strategies.MINIMUMS && monthsSooner <= 0 && interestSaved <= 0 && !!projection &&
			<span className='loan_payoff__strategy-detail'>Set a budget above your payments to see a difference</span>
			}
		</button>
	);
}

function AdHocLoanComponent({ loan, updateLoan }) {
	function onLoanChange(fields) {
		updateLoan(loan, fields);
	}

	function updateActive(event) {
		onLoanChange({ disabled: !event.target.checked });
	}

	function loanNameChange(event) {
		onLoanChange({ name: event.target.value });
	}

	function principalRemainingChange(event) {
		onLoanChange({ principal: event.target.value });
	}

	function interestChange(event) {
		onLoanChange({ interest: event.target.value });
	}

	function minimumPaymentChange(event) {
		onLoanChange({ minimumPayment: event.target.value });
	}

	function monthlyPaymentChange(event) {
		onLoanChange({ monthlyPayment: event.target.value });
	}

	function escrowAndFeesChange(event) {
		onLoanChange({ escrowAndFeesPerMonth: event.target.value });
	}

	return (
		<div className={`loan_payoff__ad_hoc ${loan.isEmpty() ? 'is-empty' : ''}`}>
			<div className='loan_payoff__ad_hoc-fields'>
				<TextInput label='Loan name' value={loan.name} onChange={loanNameChange} />
				<TextInput label='Principal remaining' value={loan.principal} onChange={principalRemainingChange} />
				<TextInput label='Interest %' value={loan.interest} onChange={interestChange} />
				<TextInput label='Minimum payment' value={loan.minimumPayment} onChange={minimumPaymentChange} />
				<TextInput label='Monthly payment' value={loan.monthlyPayment} onChange={monthlyPaymentChange} />
				<TextInput label='Fees / month' value={loan.escrowAndFeesPerMonth} onChange={escrowAndFeesChange} />
			</div>
			{!loan.isEmpty() &&
			<div className='loan_payoff__ad_hoc-footer'>
				<InputSwitch
					checked={!loan.disabled}
					onChange={updateActive}
				>
					On the graph
				</InputSwitch>
				<span>
					Paying <MoneyComponent amount={loan.getAmountPaidPerMonth() * 100} /> a month
				</span>
			</div>
			}
		</div>
	);
}

class LoanSummaryChart extends Component {
	constructor(props) {
		super(props);
		this.state = {
			opened: false,
			editingMonth: null,
		};
		this.toggleOpen = this.toggleOpen.bind(this);
		this.stopEditingMonth = this.stopEditingMonth.bind(this);
	}

	editMonth(date) {
		this.setState({ editingMonth: date.format('YYYY-MM') });
	}

	stopEditingMonth() {
		this.setState({ editingMonth: null });
	}

	toggleOpen() {
		this.setState({
			opened: !this.state.opened
		});
	}

	render() {
		const { loan, type, selectedDate } = this.props;
		const { opened, editingMonth } = this.state;
		const isLoans = type === LoanPayoff.types.LOANS;
		const isAdHoc = loan.category instanceof AdHocLoan;
		const paidOff = loan.amountRemaining <= 0;
		const isHistory = paidOff && loan.lastPaymentDate.isBefore(selectedDate, 'month');
		const balanceNow = loan.balanceAt(selectedDate);
		const minimum = Number(loan.lastKnownMinimumPayment) || 0;
		const payment = loan.ongoingPayment();
		const lastPaid = isLoans && !isAdHoc ? loan.lastPaid(selectedDate) : null;
		return (
			<div className={`loan_payoff__loan ${isHistory ? 'is-history' : ''} ${opened ? 'is-open' : ''}`}>
				<button type='button' className='btn-flat loan_payoff__loan-header' onClick={this.toggleOpen} aria-expanded={opened}>
					<span className='loan_payoff__loan-swatch' style={{ backgroundColor: loan.currentDataSet.borderColor }} />
					<span className='loan_payoff__loan-name'>{loan.category.name}</span>
					{loan.currentRate !== null &&
					<span className='loan_payoff__chip'>{loan.currentRate}%</span>
					}
					<span className='loan_payoff__loan-when'>
						{isHistory ? 'Paid off' : (isLoans ? 'Paid off by' : 'Until')}{' '}
						{loan.lastPaymentDate.format('MMM YYYY')}
					</span>
					<MaterialIcon>{opened ? 'expand_less' : 'expand_more'}</MaterialIcon>
				</button>
				{!isHistory &&
				<dl className='loan_payoff__facts'>
					<div>
						<dt>Balance {selectedDate.format('MMM')}<InfoTip>{isLoans ? metricTips.loanBalance : metricTips.savingsBalance}</InfoTip></dt>
						<dd><MoneyComponent amount={balanceNow ?? loan.amountRemaining} /></dd>
					</div>
					{isLoans && lastPaid &&
					<div>
						<dt>Paid {lastPaid.date.format('MMM')}<InfoTip>{metricTips.paid}</InfoTip></dt>
						<dd><MoneyComponent amount={lastPaid.amount} /></dd>
					</div>
					}
					{isLoans &&
					<div>
						<dt>{lastPaid ? 'Principal & interest' : 'Paying a month'}<InfoTip>{metricTips.principalAndInterest}</InfoTip></dt>
						<dd>
							<MoneyComponent amount={payment} />
							{payment !== minimum && <small> min <MoneyComponent amount={minimum} /></small>}
						</dd>
					</div>
					}
					<div>
						<dt>Interest {isLoans ? 'cost' : 'earned'}<InfoTip>{isLoans ? metricTips.interestCost : metricTips.interestEarned}</InfoTip></dt>
						<dd><MoneyComponent amount={loan.totalInterestPaid} /></dd>
					</div>
					<div>
						<dt>Principal<InfoTip>{isLoans ? metricTips.loanPrincipal : metricTips.savingsPrincipal}</InfoTip></dt>
						<dd><MoneyComponent amount={loan.startingBalance} /></dd>
					</div>
					{isLoans && !isAdHoc && downPaymentParts(loan.category.down_payments).length > 0 &&
					<div>
						<dt>Down payment<InfoTip>{metricTips.downPayment}</InfoTip></dt>
						<dd><DownPaymentSummary rows={loan.category.down_payments} /></dd>
					</div>
					}
				</dl>
				}
				{opened && isLoans && !isAdHoc &&
				<LoanTermsDetails loan={loan} />
				}
				{opened &&
				<div className='loan_payoff__schedule'>
					<table>
						<thead>
						<tr>
							<th>Month</th>
							{!isAdHoc && <th>Paid<InfoTip>{metricTips.schedulePaid}</InfoTip></th>}
							<th>Principal &amp; interest<InfoTip>{metricTips.schedulePrincipalAndInterest}</InfoTip></th>
							<th>Interest<InfoTip>{metricTips.scheduleInterest}</InfoTip></th>
							<th>Balance<InfoTip>{metricTips.scheduleBalance}</InfoTip></th>
							{!isAdHoc && <th aria-label='Edit' />}
						</tr>
						</thead>
						<tbody>
						{Array.from(loan.balanceTotals).map(([currentDate]) => {
							const key = currentDate.format('YYYY-MM');
							const paid = isAdHoc ? null : loan.paidIn(currentDate);
							const rows = [
								<tr key={key} className={currentDate.isSame(selectedDate, 'month') ? 'is-current' : ''}>
									<td>{currentDate.format('MMM YYYY')}
										{loan.manualUpdates.get(currentDate) ? <span title='A balance from the budget'> *</span> : ''}
									</td>
									{!isAdHoc &&
									<td>{paid === null ? '—' : <MoneyComponent amount={paid} />}</td>
									}
									<td><MoneyComponent amount={loan.paymentsMade.get(currentDate) || 0} /></td>
									<td><MoneyComponent amount={loan.interestsAdded.get(currentDate) || 0} /></td>
									<td><MoneyComponent amount={loan.balanceTotals.get(currentDate) || 0} /></td>
									{!isAdHoc &&
									<td className='loan_payoff__schedule-edit'>
										<button type='button' className='btn-flat' title={`Edit ${currentDate.format('MMM YYYY')}`} onClick={() => this.editMonth(currentDate)}>
											<MaterialIcon>edit</MaterialIcon>
										</button>
									</td>
									}
								</tr>
							];
							if(editingMonth === key) {
								rows.push(
									<tr key={`${key}-edit`} className='loan_payoff__month-edit'>
										<td colSpan={6}>
											<LoanMonthEditor
												category={loan.category}
												date={currentDate}
												projectedBalance={loan.balanceTotals.get(currentDate)}
												isStart={loan.beginsAt(currentDate)}
												onClose={this.stopEditingMonth}
											/>
										</td>
									</tr>
								);
							}
							return rows;
						})}
						</tbody>
					</table>
				</div>
				}
			</div>
		);
	}
}
